import { ThunkDispatch } from '@reduxjs/toolkit';
import { Map } from 'immutable';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchContents, fromContents, saveContents } from './contents';

/**
 * A custom hook that fetches and manages event contents by eventId, pageGroup, and pageName.
 * The hook automatically fetches the contents on mount and whenever the parameters change.
 *
 * @param {string} eventId - The unique identifier of the event
 * @param {string} pageGroup - The page group for the contents
 * @param {string} pageName - The page name for the contents
 * @returns {Object} An object containing:
 *   - contents: The contents data from the Redux store
 *   - loading: A boolean indicating if the contents are being loaded
 *
 * @example
 * const { contents, loading } = useFetchContents('eventId', 'group', 'name');
 * console.log(contents);
 * if (loading) {
 *    Show a loading indicator
 * }
 */
export const useFetchContents = (eventId: string, pageGroup: string, pageName: string) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  const loading = useSelector((state: Map<string, any>) =>
    fromContents.getIsLoading(state?.toJS()),
  );

  useEffect(() => {
    dispatch(fetchContents({ eventId, pageGroup, pageName }));
  }, [dispatch, eventId, pageGroup, pageName]);

  return { loading };
};

export const useEditEventContents = (eventId: string) => {
  const { loading } = useFetchContents(eventId, 'Team,Judge', 'Signup,Signup Confirmation');
  const contents = useSelector((state: Map<string, any>) => {
    const stateJS = state?.toJS();

    return {
      contents_team_signup_instructions:
        fromContents.getContent(stateJS, eventId, 'Team', 'Signup', 'Instructions')?.content ?? '',
      contents_team_signup_confirmation_notice:
        fromContents.getContent(stateJS, eventId, 'Team', 'Signup Confirmation', 'Notice')
          ?.content ?? '',
      contents_judge_signup_instructions:
        fromContents.getContent(stateJS, eventId, 'Judge', 'Signup', 'Instructions')?.content ?? '',
      contents_judge_signup_confirmation_notice:
        fromContents.getContent(stateJS, eventId, 'Judge', 'Signup Confirmation', 'Notice')
          ?.content ?? '',
    };
  });

  return { loading, contents };
};

/**
 * A custom hook to save event contents and track the saving state.
 *
 * @returns {{ saving: boolean, save: (eventId: string, contents: Record<string, any>) => void }}
 *
 * @example
 * const { save, saving } = useSaveContents();
 * save(eventId, contents); // Dispatches the save action
 * if (saving) {
 *    Show a loading indicator while saving
 * }
 */
export const useSaveContents = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const saving = useSelector((state: Map<string, any>) => fromContents.getIsSaving(state?.toJS()));

  const saveContentsFn = (eventId: string, contents: Record<string, any>) => {
    dispatch(saveContents({ eventId, contents }));
  };

  return { saveContents: saveContentsFn, saving };
};
